import types from '../mutation-types';
import FunnelAPI from '../../api/funnel';
import { dedupeInFlight } from '../utils/dedupeInFlight';

// Janela em que uma resposta já resolvida é reaproveitada por quem pede o
// mesmo dado logo depois (mesmo padrão do BATCH_REUSE_MS do kanban). Abrir o
// quadro monta a view, o seletor de funil e uma coluna por etapa, e cada um
// pedia funis e stage_stats por conta própria.
export const FUNNEL_REUSE_MS = 3000;

const stageStatsKey = (funnelId, filterParams = {}) =>
  `${funnelId}:${JSON.stringify(
    Object.keys(filterParams)
      .sort()
      .map(key => [key, filterParams[key]])
  )}`;

// No ar (sem settledAt) ou resolvido há menos de FUNNEL_REUSE_MS.
const isReusable = entry =>
  Boolean(entry) &&
  (!entry.settledAt || Date.now() - entry.settledAt < FUNNEL_REUSE_MS);

const state = {
  records: [],
  selectedFunnel: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
  stageStats: {}, // Novo: stats agregados por funnelId
  // Pedidos de stage_stats por funil + filtros: { promise, settledAt }.
  stageStatsRequests: {},
  // Última busca de funis que deu certo, para o reaproveitamento opcional.
  lastFetch: { at: null, accountId: null },
};

const getters = {
  getFunnels: $state => $state.records,
  getSelectedFunnel: $state => $state.selectedFunnel,
  getUIFlags: $state => $state.uiFlags,
  getFunnelById: state => id => {
    return state.records.find(funnel => String(funnel.id) === String(id));
  },
  getStageStats: state => funnelId => {
    return state.stageStats[funnelId] || {};
  },
};

const actions = {
  // Sidebar.vue, ChatList.vue and ContactPanel.vue each dispatch this
  // unconditionally on their own mount/init — on a cold open (first inbox
  // navigation of the session) all three can fire in the same tick, so this
  // coalesces them into a single GET /funnels round-trip.
  //
  // `reuseWithinMs` é opcional: quem só precisa da lista (o seletor, que
  // monta logo depois de o KanbanView buscar) passa a janela e recebe os
  // funis já carregados da mesma conta. Sem a opção, a busca é sempre nova,
  // como antes.
  fetch({ commit, dispatch, state, rootGetters }, options = {}) {
    const { reuseWithinMs } = options || {};
    const accountId = rootGetters?.getCurrentAccountId ?? null;
    const { lastFetch } = state;
    if (
      reuseWithinMs &&
      state.records.length > 0 &&
      lastFetch?.at &&
      lastFetch.accountId === accountId &&
      Date.now() - lastFetch.at < reuseWithinMs
    ) {
      return Promise.resolve(state.records);
    }
    return dedupeInFlight('funnel/fetch', async () => {
      commit(types.SET_FUNNEL_UI_FLAG, { isFetching: true });
      try {
        const { data } = await FunnelAPI.get();
        // Guard against poisoned cache payloads (e.g. Hash serialized as
        // [["open", n], ["awaiting_reply", n]]) which would create fake
        // funnels with empty stages and blank the Kanban board.
        if (!Array.isArray(data)) {
          throw new Error('Invalid funnels payload');
        }
        const formattedData = data
          .filter(
            funnel =>
              funnel &&
              typeof funnel === 'object' &&
              !Array.isArray(funnel) &&
              funnel.id != null &&
              funnel.name
          )
          .map(funnel => ({
            ...funnel,
            id: String(funnel.id),
            stages: Object.entries(funnel.stages || {}).reduce(
              (acc, [key, stage]) => ({
                ...acc,
                [String(key)]: {
                  ...(stage && typeof stage === 'object' ? stage : {}),
                  position: parseInt(stage?.position, 10) || 0,
                },
              }),
              {}
            ),
          }));
        commit(types.SET_FUNNELS, formattedData);
        commit('SET_FUNNELS_FETCHED', { at: Date.now(), accountId });

        // --- Fallback centralizado ---
        let fallbackFunnel = null;
        if (formattedData.length > 0) {
          // Se já existe um selectedFunnel válido, usa a cópia vinda da API (dados frescos).
          // Não reutilizar state.selectedFunnel — ficaria desatualizado após update (ex.: stage_automations).
          const currentSelectedFunnelId = state.selectedFunnel ? String(state.selectedFunnel.id) : null;
          const freshSelected = formattedData.find(f => String(f.id) === currentSelectedFunnelId);
          if (currentSelectedFunnelId && freshSelected) {
            fallbackFunnel = freshSelected;
          } else {
            fallbackFunnel = formattedData[0];
          }
        }
        await dispatch('setSelectedFunnel', fallbackFunnel);
        // ---
        return formattedData;
      } catch (error) {
        console.error('Erro ao buscar funis:', error);
        // Em caso de erro, limpa o selectedFunnel
        await dispatch('setSelectedFunnel', null);
        throw error;
      } finally {
        commit(types.SET_FUNNEL_UI_FLAG, { isFetching: false });
      }
    });
  },

  async setSelectedFunnel({ commit }, funnel) {
    // Permitir que funnel seja null para desmarcar.
    // Se funnel for inválido, seta null.
    if (!funnel || typeof funnel.id === 'undefined') {
      commit(types.SET_SELECTED_FUNNEL, null);
      return;
    }
    try {
      commit(types.SET_FUNNEL_UI_FLAG, { isUpdating: true });
      commit(types.SET_SELECTED_FUNNEL, funnel); // funnel pode ser null
    } finally {
      commit(types.SET_FUNNEL_UI_FLAG, { isUpdating: false });
    }
  },

  // Coalescing do stage_stats: o seletor e cada coluna pedem o mesmo dado ao
  // abrir o quadro (6 requests num funil de 4 etapas). Pedidos iguais (mesmo
  // funil e mesmos filtros) reusam a mesma promise enquanto ela está no ar e
  // por FUNNEL_REUSE_MS depois de chegar. Erro não é reaproveitado. As
  // releituras por mudança real (filtro, movimento, recarga) saem da coluna
  // (fetchStageStatsFromAPI) e não passam por esta janela.
  fetchStageStats(context, { funnelId, filterParams = {} }) {
    const { commit } = context;
    const key = stageStatsKey(funnelId, filterParams);
    const existing = context.state.stageStatsRequests?.[key];
    if (isReusable(existing)) return existing.promise;

    const promise = FunnelAPI.getStageStats(funnelId, filterParams)
      .then(({ data }) => {
        commit('SET_STAGE_STATS', { funnelId, stats: data.stages });
        commit('SETTLE_STAGE_STATS_REQUEST', { key, promise });
        return data.stages;
      })
      .catch(error => {
        console.error('Erro ao buscar stats das etapas:', error);
        commit('SET_STAGE_STATS', { funnelId, stats: {} });
        commit('CLEAR_STAGE_STATS_REQUEST', { key, promise });
        throw error;
      });
    commit('SET_STAGE_STATS_REQUEST', { key, promise });
    return promise;
  },
};

const mutations = {
  [types.SET_FUNNELS]($state, data) {
    $state.records = data;
  },
  [types.SET_SELECTED_FUNNEL]($state, funnel) {
    $state.selectedFunnel = funnel;
  },
  [types.SET_FUNNEL_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  SET_FUNNELS_FETCHED($state, lastFetch) {
    $state.lastFetch = lastFetch;
  },
  SET_STAGE_STATS_REQUEST($state, { key, promise }) {
    $state.stageStatsRequests = {
      ...$state.stageStatsRequests,
      [key]: { promise, settledAt: null },
    };
  },
  SETTLE_STAGE_STATS_REQUEST($state, { key, promise }) {
    const entry = $state.stageStatsRequests?.[key];
    if (!entry || entry.promise !== promise) return;
    $state.stageStatsRequests = {
      ...$state.stageStatsRequests,
      [key]: { promise, settledAt: Date.now() },
    };
  },
  CLEAR_STAGE_STATS_REQUEST($state, { key, promise }) {
    const entry = $state.stageStatsRequests?.[key];
    if (!entry || entry.promise !== promise) return;
    const next = { ...$state.stageStatsRequests };
    delete next[key];
    $state.stageStatsRequests = next;
  },
  SET_STAGE_STATS($state, { funnelId, stats }) {
    $state.stageStats = {
      ...$state.stageStats,
      [funnelId]: stats,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
